import type {ReactNode, TextareaHTMLAttributes} from 'react';

import {forwardRef, useId} from 'react';

import {cn} from '../../helpers/cn';

export type TextAreaSize = 'sm' | 'md' | 'lg';
export type TextAreaStatus = 'error' | 'warning';

export type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
  size?: TextAreaSize;
  status?: TextAreaStatus;
  disabled?: boolean;
  label?: ReactNode;
  required?: boolean;
  help?: ReactNode;
  className?: string;
  wrapperClassName?: string;
  rows?: number;
  textareaClassName?: string;
  placeHolder?: string;
  title?: ReactNode;
  error?: string;
  width?: string;
  height?: string;
  borderColor?: string;
};

const sizeClasses: Record<TextAreaSize, string> = {
  sm: 'min-h-20 px-[11px] py-2 text-[12px] leading-[18px]',
  md: 'min-h-24 px-[11px] py-2 text-[14px] leading-[22px]',
  lg: 'min-h-28 px-[11px] py-2 text-[14px] leading-[24px]'
};

function statusBorderClasses(status?: TextAreaStatus, disabled?: boolean) {
  if (disabled) return 'border-disabled-border bg-disabled-surface';
  if (status === 'error') return 'border-destructive bg-white hover:border-destructive';
  if (status === 'warning') return 'border-yellow bg-white hover:border-yellow';
  return 'border-disabled-border bg-white hover:border-orange';
}

function statusFocusClasses(status?: TextAreaStatus, disabled?: boolean) {
  if (disabled) return '';
  if (status === 'error') {
    return 'focus-within:border-destructive focus-within:shadow-[0_0_0_2px] focus-within:shadow-destructive-surface';
  }
  if (status === 'warning') {
    return 'focus-within:border-yellow focus-within:shadow-[0_0_0_2px] focus-within:shadow-yellow-soft';
  }
  return 'focus-within:border-orange focus-within:shadow-[0_0_0_2px] focus-within:shadow-brand-surface';
}

function TextAreaFieldShell({
  id,
  label,
  required,
  help,
  showHelp,
  wrapperClassName,
  children
}: {
  id?: string;
  label?: ReactNode;
  required?: boolean;
  help?: ReactNode;
  showHelp: boolean;
  wrapperClassName?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex w-full flex-col gap-1.5', wrapperClassName)}>
      {label ? (
        <label htmlFor={id} className="text-[14px] font-medium leading-[21px] text-gray-08">
          {label}
          {required ? <span className="text-destructive">*</span> : null}
        </label>
      ) : null}
      {children}
      {showHelp && help ? (
        <div className="flex items-center gap-1.5 px-2 text-[10px] leading-4 text-destructive">
          <i className="icon-Info-Outlined text-[16px]" aria-hidden />
          <span>{help}</span>
        </div>
      ) : null}
    </div>
  );
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    size = 'lg',
    status,
    disabled,
    label,
    required,
    help,
    className,
    wrapperClassName,
    textareaClassName,
    rows,
    id,
    placeHolder,
    placeholder,
    title,
    error,
    width,
    height,
    borderColor,
    style,
    onFocus,
    onBlur,
    ...props
  },
  ref
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const resolvedLabel = label ?? title;
  const resolvedPlaceholder = placeholder ?? placeHolder;
  const resolvedStatus = status ?? (error ? 'error' : undefined);
  const resolvedHelp = help ?? error;
  const showHelp = Boolean(required && resolvedHelp);
  const wrapperStyle = {width, height, borderColor, ...style};

  return (
    <TextAreaFieldShell
      id={textareaId}
      label={resolvedLabel}
      required={required}
      help={resolvedHelp}
      showHelp={showHelp}
      wrapperClassName={wrapperClassName}
    >
      <span
        className={cn(
          'inline-flex w-full rounded-[6px] border transition-[border-color,box-shadow]',
          sizeClasses[size],
          statusBorderClasses(resolvedStatus, disabled),
          statusFocusClasses(resolvedStatus, disabled),
          disabled && 'opacity-50',
          className
        )}
        style={wrapperStyle}
      >
        <textarea
          {...props}
          ref={ref}
          id={textareaId}
          rows={rows}
          disabled={disabled}
          placeholder={resolvedPlaceholder}
          aria-invalid={resolvedStatus === 'error' || undefined}
          className={cn(
            'min-h-0 w-full resize-none border-0 bg-transparent font-iransans font-medium text-gray-08 outline-none placeholder:font-normal placeholder:text-gray-06 disabled:cursor-not-allowed disabled:text-disabled-fg',
            textareaClassName
          )}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </span>
    </TextAreaFieldShell>
  );
});

TextArea.displayName = 'TextArea';
